import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { AVAILABLE_LOCALES, detectLocale, translate, type Locale } from '@/lib/i18n';
import { setCookie } from '@/lib/cookies';

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: string, ...args: Array<string | number>) => string;
  available: typeof AVAILABLE_LOCALES;
}

const I18nCtx = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale());

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    setCookie('kispro-lang', l, { days: 365, crossDomain: true });
    document.documentElement.lang = l;
  }, []);

  const t = useCallback(
    (key: string, ...args: Array<string | number>) => translate(locale, key, ...args),
    [locale],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t, available: AVAILABLE_LOCALES }),
    [locale, setLocale, t],
  );

  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nCtx);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}
